import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Output, TemplateRef, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IProductConfigurableSelection } from '@df/catalog/product/product';
import { ElementHeightDirective } from '@df/dom/element-height/element-height.directive';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { GiftcardsProductComponentDef } from '@df/module-giftcards/giftcards-product.component.def';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { LoqateEmailValidateDirective } from '@df/module-loqate/email/loqate-email.validator';
import { OnChange } from '@df/ng/on-change';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { OrdinalPipe } from '@df/ui/display/ordinal.pipe';
import { MatchCheckDirective } from '@df/ui/form/match-check.directive';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { ModalService } from '@df/ui/modal/modal.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { StickyFreeComponent } from '../../common/sticky-free/sticky-free.component';
import { CalendarComponent } from '../../ui/form/calendar.component';
import { InputWrapBodyDirective } from '../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../ui/form/input-wrap.component';
import { MuiInputDirective } from '../../ui/form/mui-input.directive';
import { ValidatePatternAsyncDirective } from '../../ui/form/validate/validate-pattern-acync.directive';
import { GiftcardsAddToBasketComponent } from './giftcards-add-to-basket.component';
import { GiftcardsAmountSelectorComponent } from './giftcards-amount-selector.component';
import { GiftcardsProductConfigurableOptionsComponent } from './giftcards-product-configurable-options.component';

const datePipe = new DatePipe('en-GB');

@Component({
    selector: 'giftcards-product',
    templateUrl: './giftcards-product.html',
    styleUrl: './giftcards-product.component.scss',
    standalone: true,
    imports: [
        CalendarComponent,
        CommonModule,
        DfImageDirective,
        ElementHeightDirective,
        FormsModule,
        GiftcardsAddToBasketComponent,
        GiftcardsAmountSelectorComponent,
        GiftcardsProductConfigurableOptionsComponent,
        GridModule,
        InputWrapBodyDirective,
        InputWrapComponent,
        LoqateEmailValidateDirective,
        MatchCheckDirective,
        MuiInputDirective,
        OrdinalPipe,
        StickyFreeComponent,
        TranslatePipe,
        ValidatePatternAsyncDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GiftcardsProductComponent extends GiftcardsProductComponentDef {
    @Output()
    readonly selectNext = new EventEmitter<void>();

    @ViewChild('secondStep', { static: false })
    secondStep!: ElementRef;

    @ViewChild('thirdStep', { static: false })
    thirdStep!: ElementRef;

    @ViewChild('fourthStep', { static: false })
    fourthStep!: ElementRef;

    @ViewChild('calendarModal')
    calendarModal!: TemplateRef<any>;

    private _calendarModalRef?: ModalRef<TemplateRef<any>>;
    protected override canDetach = false;

    @OnChange(function () {
        if (this.initiated) {
            this.detectChanges();
        }
    })
    formType?: string;

    sendGiftCardImmediately?: boolean = true;
    isCalendarOpened = false;
    elementBlur = {};
    recipient_email_confirmation?: string;

    protected modalService = inject(ModalService);
    protected scrollToService = inject(ScrollToService);

    handleDataChange(data?: IProductConfigurableSelection) {
        this.detectChanges();
    }

    handleInitChange(data: IProductConfigurableSelection) {
        this.matchBest(data);

        this.handleDataChange(data);
    }

    handleSelectionChange(data: IProductConfigurableSelection) {
        this.matchBest(data);

        this.handleDataChange(data);

        this.goToSecondStep();
    }

    handleKeyUp(event: Event, id: string, field: string) {
        if (event && this.elementBlur[id] && !field?.length) {
            this.elementBlur[id] = false;

            this.detectChanges();
        } else if (!event) {
            this.elementBlur[id] = field?.length;

            this.detectChanges();
        }
    }

    /**
     * calendar modal
     */
    openCalendarModal() {
        if (this.client.isS && this.isCalendarOpened) {
            this._calendarModalRef = this.modalService.open(
                this.calendarModal,
                {
                    minWidth: '100%',
                    modalClass: 'calendar-modal',
                    position: {
                        bottom: '0'
                    }
                },
                'bottom'
            );

            this.subscriptions.add(
                this._calendarModalRef.beforeClosed().subscribe(() => {
                    this.isCalendarOpened = false;
                    this.detectChanges();
                })
            );
        }
    }

    /**
     * close calendar modal
     */
    closeCalendarModal() {
        this.detectChanges();

        setTimeout(() => {
            this._calendarModalRef?.close();
        }, 500);
    }

    get today() {
        const date = new Date();
        return date;
    }

    toggleCalendar(open: boolean = true): void {
        this.isCalendarOpened = open;
        this.detectChanges();
    }

    chooseFormType(value: string) {
        this.formType = value;
        this.detectChanges();
    }

    get deliveryDate() {
        return this.request.am_giftcard_date_delivery;
    }

    set deliveryDate(date) {
        if (date && date !== this.request.am_giftcard_date_delivery) {
            this.request.am_giftcard_date_delivery = date;
            this.isCalendarOpened = false;
        }
        this.detectChanges();
    }

    get firstStepChecked() {
        return !!this.request.am_giftcard_image;
    }

    get secondStepChecked() {
        return this.firstStepChecked && (this.request.am_giftcard_amount_custom || this.request.am_giftcard_amount);
    }

    get thirdStepChecked() {
        return (
            this.firstStepChecked &&
            this.secondStepChecked &&
            this.request.am_giftcard_recipient_name &&
            this.request.am_giftcard_sender_name
        );
    }

    get isValid() {
        if (this.isPhysical) {
            return this.request.am_giftcard_amount_custom || this.request.am_giftcard_amount;
        } else {
            return this.thirdStepChecked && !!this.formType;
        }
    }

    goToStep(element: ElementRef): void {
        if (this.app.client.isS && this.isVirtual && element?.nativeElement) {
            setTimeout(() => {
                this.scrollToService.scrollTo(element.nativeElement, {
                    offset: 50
                });
            }, 500);
        }
    }

    goToSecondStep(): void {
        if (this.firstStepChecked && this.secondStep) {
            this.goToStep(this.secondStep);
        }
    }

    goToThirdStep() {
        if (this.secondStepChecked && this.thirdStep) {
            this.goToStep(this.thirdStep);
        }
    }

    goToFourthStep() {
        if (this.thirdStepChecked && this.fourthStep) {
            this.goToStep(this.fourthStep);
        }
    }

    handleSelectNext(): void {
        this.selectNext.emit();
    }

    protected override onModelChange() {
        super.onModelChange();

        this.app.events.broadcast('product-configurable-options.model-change');
    }
}
